import { useState, useCallback, useMemo } from 'react';
import { validateRange, type TRangeSchema } from './validateRange';

type TNumericKeys<T> = {
  [K in keyof T]: T[K] extends number | null ? K : never;
}[keyof T];

interface IFilterContext<T> {
  localFilters: T,
  setLocalFilters: (updater: T | ((prev: T) => T)) => void,
}

interface IUseRangeFilterProps<T> {
  fromKey: TNumericKeys<T>,
  toKey: TNumericKeys<T>,
  schema?: TRangeSchema,
}

interface IRangeFieldMeta {
  touched: boolean,
  error: string | undefined,
}

interface IUseRangeFilterReturn {
  minValue: string,
  maxValue: string,
  minMeta: IRangeFieldMeta,
  maxMeta: IRangeFieldMeta,
  handleMinChange: (e: React.ChangeEvent<HTMLInputElement>) => void,
  handleMaxChange: (e: React.ChangeEvent<HTMLInputElement>) => void,
  hasError: boolean,
}

const toDisplay = (value: number | null): string => (value === null ? '' : String(value));
const toNumber = (value: string): number | null => (value === '' ? null : Number(value));
const normalize = (raw: string): string => raw.replace(',', '.');

const createUseRangeFilter = <T extends Record<string, unknown>>(
  useFilterContext: () => IFilterContext<T>,
) => (
  { fromKey, toKey, schema }: IUseRangeFilterProps<T>,
): IUseRangeFilterReturn => {
  const { localFilters, setLocalFilters } = useFilterContext();

  const [minValue, setMinValue] = useState(() => toDisplay(localFilters[fromKey] as number | null));
  const [maxValue, setMaxValue] = useState(() => toDisplay(localFilters[toKey] as number | null));

  const { minError, maxError } = useMemo(() => validateRange(minValue, maxValue, schema), [minValue, maxValue, schema]);
  const hasError = !!(minError || maxError);

  const handleMinChange = useCallback((e: React.ChangeEvent<HTMLInputElement>): void => {
    const raw = normalize(e.target.value);
    setMinValue(raw);
    setLocalFilters((prev) => ({ ...prev, [fromKey]: toNumber(raw) }));
  }, [fromKey, setLocalFilters]);

  const handleMaxChange = useCallback((e: React.ChangeEvent<HTMLInputElement>): void => {
    const raw = normalize(e.target.value);
    setMaxValue(raw);
    setLocalFilters((prev) => ({ ...prev, [toKey]: toNumber(raw) }));
  }, [toKey, setLocalFilters]);

  const minMeta: IRangeFieldMeta = { touched: true, error: minError };
  const maxMeta: IRangeFieldMeta = { touched: true, error: maxError };

  return { minValue, maxValue, minMeta, maxMeta, handleMinChange, handleMaxChange, hasError };
};

export { createUseRangeFilter };
export type { IUseRangeFilterProps, IUseRangeFilterReturn, IFilterContext, IRangeFieldMeta };
